/**
 * @fileoverview Input/output schemas for chat-with-story-v1.
 *
 * Unlike primitive agents that take context macros and return structured
 * JSON, this conversational flow takes the full in-memory Project plus a
 * new user message, and returns the updated assistant turn. Streaming is
 * handled separately via Genkit's streaming callback — the resolved
 * output is just the final assistant message so the flow has a sensible
 * non-streaming return shape for logs and replays.
 */

import { z } from 'zod';
import type { Project } from '@/domains/projects/schemas';
import {
  ChatMessageSchema,
  ChatContextHintSchema,
} from '@/domains/chat/schemas';

// =============================================================================
// INPUT
// =============================================================================

/**
 * Input to the chat flow.
 *
 * The caller (a server action) hands over the full Project so tools can
 * read any part of it in-memory, plus the prior conversation and the new
 * user message for this turn. Unlike primitive agents, we don't take
 * prompt-variable context macros — the system prompt is built from the
 * Project directly via `buildStoryIndex`.
 */
export const ChatWithStoryInputSchema = z.object({
  /**
   * The full, rehydrated Project — tool handlers read from this via context.
   *
   * Not validated against `ProjectSchema` at the flow boundary: the project
   * is a server-trusted object (ownership is verified before load), legacy
   * documents in Firestore can violate the strict schema on fields this
   * flow never reads, and TypeScript already enforces the shape at call
   * sites. `z.custom` preserves the `Project` type without runtime parsing.
   */
  project: z.custom<Project>(() => true),

  /**
   * Prior conversation turns, newest last. May be empty for a brand new
   * session. User and assistant turns from earlier in this session are
   * replayed to the model on every call so it has full conversational
   * memory within the session.
   */
  history: z.array(ChatMessageSchema).default([]),

  /** The new user turn to respond to. */
  userMessage: z
    .string()
    .min(1, 'userMessage must not be empty')
    .describe('The plain-text user message for this turn.'),

  /**
   * Optional location hint — where the user is in the app (e.g., the
   * chapter they are currently viewing). Injected into the system prompt
   * so the assistant can ground its reply without the user having to
   * spell it out.
   */
  contextHint: ChatContextHintSchema.optional(),

  /**
   * Model identifier, e.g. `google-ai/gemini-3-flash-preview`. Defaults
   * to the agent's recommended model when not supplied.
   */
  model: z.string().optional(),
});
export type ChatWithStoryInput = z.infer<typeof ChatWithStoryInputSchema>;

// =============================================================================
// OUTPUT
// =============================================================================

/**
 * Output of the chat flow — just the fully-assembled assistant message
 * (content plus any tool calls made during the turn). The caller is
 * responsible for appending this to the persisted session.
 */
export const ChatWithStoryOutputSchema = z.object({
  assistantMessage: ChatMessageSchema,
});
export type ChatWithStoryOutput = z.infer<typeof ChatWithStoryOutputSchema>;

// =============================================================================
// STREAMING
// =============================================================================

/**
 * Shape of events emitted on the streaming channel as the flow runs.
 *
 * - `text`: incremental content tokens for the assistant's reply.
 * - `tool-call-start`: a tool is about to run — the UI uses this to render
 *   "Reading Chapter 4..." indicators.
 * - `tool-call-end`: the tool finished (success or error).
 * - `done`: terminal event carrying the full assistant message.
 */
export const ChatStreamChunkSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), delta: z.string() }),
  z.object({
    type: z.literal('tool-call-start'),
    toolCallId: z.string(),
    name: z.string(),
    input: z.unknown().optional(),
  }),
  z.object({
    type: z.literal('tool-call-end'),
    toolCallId: z.string(),
    error: z.string().optional(),
  }),
  z.object({
    type: z.literal('done'),
    assistantMessage: ChatMessageSchema,
  }),
]);
export type ChatStreamChunk = z.infer<typeof ChatStreamChunkSchema>;
